// @givmo/board — EVERY word CBG-YR3-ROLES-PRESIDING's and CBG-FIX57's screens add (backend
// `board_minutes.md` §P9–§P12): who presided at a meeting, recorded in "Who was there", and the
// minutes a meeting's "Record a motion" may approve.
//
// ══ WHICH WORDS ARE APPROVED ════════════════════════════════════════════════════════════════════
//
// All of them. Jonathan approved the frames for building on 2026-10-13 22:40 PDT ("what the hego with
// your recs") and every line here in copy round CBG-YR3-ROLES-PRESIDING-COPY1 on 2026-10-14 09:53 PDT
// ("go with your recs"; `scratchpad/2026-10-14-presiding-copy-round/copy-review.html`), each exactly as
// that page shows it. Each line names the frame that draws it (`p1-who-presided`, `p2-presided-dialog`,
// `p4-motion`, `x1-held-motion`, `x1b-picker`; the design branch `design/board-four-frames` @
// `25fd20eb`), or "not drawn" for a state no frame shows. The panel's own "Not recorded", "Record" and
// "Change", and RC11's motion words ("Who presided", the note under "Approves", the prefilled motion),
// are reused as they are, not repeated here; the one shipped line repeated here (`recordFailed`) says
// where it ships.
//
// The record law: every line here is SHOWN ONLY. Nothing stores one, so no stored record moves with
// its words.
//
// ⚠ Plan §3.2 and §3.4 bind every line: what the board recorded, never a conclusion. The board's
// duties only SUGGEST who presided; the officer records it. No pronoun for a named person.

/** CBG-YR3-ROLES-PRESIDING's and CBG-FIX57's words, all approved; one shipped line reused. */
export const presidingWords = {
  // ── "Who was there" (frames p1, p2) ───────────────────────────────────────────────────────────

  // Drawn, p2 (the dialog's title). Shown only.
  dialogTitle: 'Who presided at this meeting?',
  // Drawn, p2 — the pattern "Suggested by your board’s duties: the {title} signs the minutes."
  // (CBG-YR3-ROLES-COPY3) with layer 3's duty words. Shown only.
  suggestedPresides: (title: string) =>
    `Suggested by your board’s duties: the ${title} presides at meetings.`,
  // Not drawn (the in-absence title) — the same pattern and duty words. Shown only.
  suggestedPresidesInAbsence: (title: string) =>
    `Suggested by your board’s duties: the ${title} presides when the presiding officer is absent.`,
  // Drawn, p2. The quoted words are the minutes' own, served by the presiding read
  // (`minutes_name`), so the line quotes exactly what the minutes print. Shown only.
  minutesWillSay: (minutesName: string) => `The minutes will say “${minutesName}”.`,
  // Drawn, p2. Shown only.
  onlyPresent: 'Only people recorded as present can be chosen.',
  // Reused as shipped, not new (the recorded-assent dialog's and the calendar's `failed`): a failed
  // record, when the backend sent no sentence of its own.
  recordFailed: 'We couldn’t record that. Please try again.',

  // ── The motion dialog (frame p4) ──────────────────────────────────────────────────────────────

  // Drawn, p4 — under "Who presided", while it holds the person recorded in "Who was there". Shown
  // only.
  fromWhoWasThere:
    'From “Who was there” at this meeting. Change it if someone else presided for this vote.',

  // ── The motion dialog's picker (frames x1-held-motion, x1b-picker; CBG-FIX57) ───────────────

  // Drawn, x1 and x1b. Shown only.
  approvesMinutes: 'Approves minutes (optional)',
  // Drawn, x1b (the field with nothing chosen). Shown only.
  noMinutes: 'No minutes',
  // Drawn, x1. Shown only.
  approvesMinutesNote: 'Minutes from earlier meetings that your board hasn’t approved yet.',
  // Drawn, x1b — a meeting's own minutes, by its title and day. Shown only.
  choice: (meeting: string, date: string) => `${meeting}, ${date}`,
  // Drawn, x1b — a closed session's own minutes, by public facts only: its general purpose, never
  // its title. Shown only.
  closedChoice: (meeting: string, purpose: string | null) =>
    purpose !== null && purpose.trim().length > 0
      ? `${meeting} · Closed session (${purpose.trim()})`
      : // Not drawn — a closed session with no general purpose recorded. Shown only.
        `${meeting} · Closed session`,
} as const;
